/**
 * ServiceBadge Component
 *
 * Displays a small badge indicating service availability for an identity.
 * Used in the MentionAutocomplete dropdown to show which services
 * a user is connected to.
 */

import { Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface ServiceBadgeProps {
  /** Service type (slack, email, teams, google) */
  service: string;
  /** Whether the service is connected/available (default: true) */
  available?: boolean;
  /** Size of the badge (default: sm) */
  size?: "xs" | "sm" | "md";
  /** Whether to show the label in tooltip (default: true) */
  showLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Service configuration with icons and colours.
 */
const SERVICE_CONFIG: Record<string, { icon: string; label: string; colour: string }> = {
  slack: {
    icon: "logos:slack-icon",
    label: "Slack",
    colour: "#4A154B",
  },
  email: {
    icon: "mdi:email",
    label: "Email",
    colour: "#EA4335",
  },
  teams: {
    icon: "logos:microsoft-teams",
    label: "Teams",
    colour: "#6264A7",
  },
  google: {
    icon: "logos:google-icon",
    label: "Google",
    colour: "#4285F4",
  },
  jira: {
    icon: "logos:jira",
    label: "Jira",
    colour: "#0052CC",
  },
  github: {
    icon: "logos:github-icon",
    label: "GitHub",
    colour: "#181717",
  },
  microsoft: {
    icon: "logos:microsoft-icon",
    label: "Microsoft",
    colour: "#00A4EF",
  },
  linear: {
    icon: "simple-icons:linear",
    label: "Linear",
    colour: "#5E6AD2",
  },
  notion: {
    icon: "logos:notion-icon",
    label: "Notion",
    colour: "#000000",
  },
};

/** Size configurations */
const SIZE_CONFIG = {
  xs: { badge: "w-3 h-3", icon: 8 },
  sm: { badge: "w-5 h-5", icon: 12 },
  md: { badge: "w-6 h-6", icon: 14 },
};

/**
 * ServiceBadge displays a small icon indicating whether a user
 * is connected to a particular service.
 *
 * @example
 * <ServiceBadge service="slack" available={true} />
 * <ServiceBadge service="teams" available={false} />
 * <ServiceBadge service="slack" size="xs" showLabel={false} />
 */
export const ServiceBadge: React.FC<ServiceBadgeProps> = ({
  service,
  available = true,
  size = "sm",
  showLabel = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const config = SERVICE_CONFIG[service.toLowerCase()];
  const sizeConfig = SIZE_CONFIG[size];

  if (!config) {
    return null;
  }

  const tooltipContent = available
    ? t("{{service}} connected", { service: config.label })
    : t("{{service}} not connected", { service: config.label });

  const badge = (
    <span
      className={`inline-flex items-center justify-center ${sizeConfig.badge} rounded-full transition-opacity ${className}`}
      style={{
        backgroundColor: available ? config.colour : "#9CA3AF",
        opacity: available ? 1 : 0.4,
      }}
    >
      <Icon
        className="text-white"
        height={sizeConfig.icon}
        icon={config.icon}
        style={{
          filter: available ? "none" : "grayscale(100%)",
        }}
        width={sizeConfig.icon}
      />
    </span>
  );

  // If showLabel is false, return badge without tooltip
  if (!showLabel) {
    return badge;
  }

  return (
    <Tooltip content={tooltipContent} placement="top" size="sm">
      {badge}
    </Tooltip>
  );
};

/**
 * Services supported for mention search in the autocomplete dropdown.
 */
export const SUPPORTED_SERVICES = ["slack", "jira", "google"];

/**
 * Get service configuration by name.
 */
export const getServiceConfig = (service: string) => SERVICE_CONFIG[service.toLowerCase()];

export default ServiceBadge;
